import { z } from 'zod';

import {
  DEFAULT_MAX_CONCURRENT_TRANSFERS,
  MAX_CONCURRENT_TRANSFERS,
  MAX_DEVICE_NAME_LENGTH,
  MAX_FILE_BYTES,
  MAX_ICE_CANDIDATE_LENGTH,
  MAX_REQUEST_ID_LENGTH,
  MAX_ROOM_CAPACITY,
  MAX_SDP_LENGTH,
  MIN_ROOM_CAPACITY,
  ROOM_CODE_LENGTH,
  ROOM_CODE_PATTERN,
  TRANSFER_MAX_CHUNK_SIZE,
  TRANSFER_MIN_CHUNK_SIZE,
} from './constants.js';
import { ERROR_CODES } from './errors.js';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/; // eslint-disable-line no-control-regex

export const timestampSchema = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);

export const requestIdSchema = z
  .string()
  .min(1)
  .max(MAX_REQUEST_ID_LENGTH)
  .regex(/^[A-Za-z0-9._:-]+$/, 'Request IDs may only contain alphanumerics and . _ : -');

/** Internal room identifier. Never accepted from clients. */
export const roomIdSchema = z
  .string()
  .regex(/^rm_[A-Za-z0-9_-]{16,64}$/, 'Malformed room id');

export const roomCodeSchema = z
  .string()
  .trim()
  .max(12)
  .transform((value) => value.toUpperCase())
  .pipe(
    z
      .string()
      .length(ROOM_CODE_LENGTH)
      .regex(ROOM_CODE_PATTERN, 'Room codes use six characters: A–Z and 2–9'),
  );

export const peerIdSchema = z.string().regex(/^p_[0-9a-f]{16}$/, 'Malformed peer id');

export const peerRoleSchema = z.enum(['host', 'guest']);

export const deviceNameSchema = z
  .string()
  .trim()
  .min(1, 'Device name is required')
  .max(MAX_DEVICE_NAME_LENGTH, `Device name must be at most ${MAX_DEVICE_NAME_LENGTH} characters`)
  .refine((value) => !CONTROL_CHARS.test(value), 'Device name contains control characters');

/** 32 random bytes, base64url encoded. Only ever sent over TLS. */
export const hostTokenSchema = z
  .string()
  .min(32)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'Malformed access token');

export const transferIdSchema = z
  .string()
  .regex(/^[0-9a-f]{32}$/, 'Malformed transfer id');

export const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/, 'Malformed SHA-256 digest');

export const errorCodeSchema = z.enum(ERROR_CODES);

export const peerConnectionStateSchema = z.enum([
  'idle',
  'connecting',
  'connected',
  'disconnected',
  'failed',
  'closed',
]);

export const transferStatusSchema = z.enum([
  'waiting',
  'connecting',
  'transferring',
  'completed',
  'failed',
  'cancelled',
]);

export const transferDirectionSchema = z.enum(['send', 'receive']);

export const transferIntegritySchema = z.enum(['pending', 'verified', 'mismatch']);

/* -------------------------------------------------------------------------- */
/* Domain objects                                                             */
/* -------------------------------------------------------------------------- */

export const peerInfoSchema = z.object({
  id: peerIdSchema,
  name: deviceNameSchema,
  role: peerRoleSchema,
  joinedAt: timestampSchema,
});

export const roomInfoSchema = z.object({
  code: roomCodeSchema,
  createdAt: timestampSchema,
  expiresAt: timestampSchema,
  capacity: z.number().int().min(MIN_ROOM_CAPACITY).max(MAX_ROOM_CAPACITY),
  peerCount: z.number().int().nonnegative().max(MAX_ROOM_CAPACITY),
  hostPeerId: peerIdSchema.nullable(),
});

/** A peer as rendered by the browser, including live WebRTC state. */
export const peerViewSchema = peerInfoSchema.extend({
  connectionState: peerConnectionStateSchema,
  connectedAt: timestampSchema.nullable(),
  latencyMs: z.number().int().nonnegative().nullable(),
  isSelf: z.boolean(),
});

export const fileMetaSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(255)
      .refine((value) => !/[/\\]/.test(value), 'File names cannot contain path separators')
      .refine((value) => value !== '.' && value !== '..', 'Invalid file name'),
    size: z.number().int().nonnegative().max(MAX_FILE_BYTES),
    mimeType: z
      .string()
      .max(160)
      .regex(/^[\w.+-]+\/[\w.+-]+(;.*)?$/, 'Malformed media type')
      .nullable()
      .optional(),
    relativePath: z
      .string()
      .max(1024)
      .refine((value) => !value.startsWith('/') && !value.split('/').includes('..'), 'Unsafe path')
      .optional(),
    lastModified: timestampSchema.optional(),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/* WebRTC signal payloads                                                     */
/* -------------------------------------------------------------------------- */

export const sdpDescriptionSchema = z
  .object({
    type: z.enum(['offer', 'answer']),
    sdp: z.string().min(1).max(MAX_SDP_LENGTH),
  })
  .strict();

export const iceCandidateSchema = z
  .object({
    candidate: z.string().max(MAX_ICE_CANDIDATE_LENGTH),
    sdpMid: z.string().max(256).nullable().optional(),
    sdpMLineIndex: z.number().int().min(0).max(4096).nullable().optional(),
    usernameFragment: z.string().max(256).nullable().optional(),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/* Transfer control protocol (runs over the WebRTC control data channel)      */
/* -------------------------------------------------------------------------- */

export const chunkSizeSchema = z
  .number()
  .int()
  .min(TRANSFER_MIN_CHUNK_SIZE)
  .max(TRANSFER_MAX_CHUNK_SIZE);

const transferFailureReasonSchema = z.string().trim().min(1).max(240);

export const transferOfferPayloadSchema = z
  .object({
    transferId: transferIdSchema,
    file: fileMetaSchema,
    chunkSize: chunkSizeSchema,
  })
  .strict();

export const transferAcceptPayloadSchema = z
  .object({
    transferId: transferIdSchema,
    /** Receiver may request a smaller chunk size than the sender proposed. */
    chunkSize: chunkSizeSchema.optional(),
  })
  .strict();

export const transferRejectPayloadSchema = z
  .object({
    transferId: transferIdSchema,
    reason: transferFailureReasonSchema.optional(),
    code: errorCodeSchema.optional(),
  })
  .strict();

export const fileCompletePayloadSchema = z
  .object({
    transferId: transferIdSchema,
    sha256: sha256HexSchema,
    size: z.number().int().nonnegative().max(MAX_FILE_BYTES),
    chunks: z.number().int().nonnegative(),
  })
  .strict();

export const transferDonePayloadSchema = z
  .object({
    transferId: transferIdSchema,
  })
  .strict();

export const transferCancelPayloadSchema = z
  .object({
    transferId: transferIdSchema,
    by: z.enum(['sender', 'receiver']),
    reason: transferFailureReasonSchema.optional(),
  })
  .strict();

export const transferErrorPayloadSchema = z
  .object({
    transferId: transferIdSchema,
    code: errorCodeSchema,
    message: z.string().max(240).optional(),
  })
  .strict();

export type TransferOfferPayload = z.infer<typeof transferOfferPayloadSchema>;
export type TransferAcceptPayload = z.infer<typeof transferAcceptPayloadSchema>;
export type TransferRejectPayload = z.infer<typeof transferRejectPayloadSchema>;
export type FileCompletePayload = z.infer<typeof fileCompletePayloadSchema>;
export type TransferDonePayload = z.infer<typeof transferDonePayloadSchema>;
export type TransferCancelPayload = z.infer<typeof transferCancelPayloadSchema>;
export type TransferErrorPayload = z.infer<typeof transferErrorPayloadSchema>;

export const transferMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('transfer_offer'), payload: transferOfferPayloadSchema }),
  z.object({ type: z.literal('transfer_accept'), payload: transferAcceptPayloadSchema }),
  z.object({ type: z.literal('transfer_reject'), payload: transferRejectPayloadSchema }),
  z.object({ type: z.literal('file_complete'), payload: fileCompletePayloadSchema }),
  z.object({ type: z.literal('transfer_done'), payload: transferDonePayloadSchema }),
  z.object({ type: z.literal('transfer_cancel'), payload: transferCancelPayloadSchema }),
  z.object({ type: z.literal('transfer_error'), payload: transferErrorPayloadSchema }),
]);

/* -------------------------------------------------------------------------- */
/* API response shapes                                                        */
/* -------------------------------------------------------------------------- */

export const apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    requestId: z.string().optional(),
  }),
});

export const settingStunServersSchema = z
  .array(z.string().trim().min(1).max(256))
  .max(8)
  .default([]);

export const turnConfigSchema = z
  .object({
    urls: z.array(z.string().trim().min(1).max(512)).max(8),
    username: z.string().max(256).optional(),
    credential: z.string().max(256).optional(),
  })
  .nullable()
  .default(null);

export const userSettingsSchema = z.object({
  deviceName: z.string().trim().min(1).max(MAX_DEVICE_NAME_LENGTH),
  theme: z.enum(['system', 'light', 'dark']),
  stunServers: settingStunServersSchema,
  turn: turnConfigSchema,
  autoAcceptTransfers: z.boolean(),
  autoDownload: z.boolean(),
  maxConcurrentTransfers: z
    .number()
    .int()
    .min(1)
    .max(MAX_CONCURRENT_TRANSFERS)
    .default(DEFAULT_MAX_CONCURRENT_TRANSFERS),
});

export const createRoomResponseSchema = z.object({
  room: roomInfoSchema,
  /** Returned exactly once, at creation time. Never exposed publicly. */
  hostToken: hostTokenSchema,
  joinPath: z.string().max(512),
  wsPath: z.string().max(512),
});

export const roomResponseSchema = z.object({
  room: roomInfoSchema,
});

export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string(),
  protocolVersion: z.number().int(),
  now: timestampSchema,
  region: z.string().nullable(),
});

export const createRoomRequestSchema = z
  .object({
    capacity: z.number().int().min(MIN_ROOM_CAPACITY).max(MAX_ROOM_CAPACITY).optional(),
  })
  .strict();

export { DEFAULT_MAX_CONCURRENT_TRANSFERS };
